import type { Tables } from "./types";

export function generateId({
	table,
	createdAt,
	postfix,
}: {
	table: Tables;
	createdAt: string;
	postfix?: string;
}): string {
	return `${singularize(table)}-${sumNumbersInIsoString(createdAt)}-${crypto.randomUUID()}${postfix ? `-${postfix}` : ""}`;
}

function singularize(value: string): string {
	if (!value) {
		return value;
	}

	const parts = value.split("_");
	const last = parts[parts.length - 1];
	parts[parts.length - 1] = singularizeWord(last);

	return parts.join("_");
}

function singularizeWord(word: string): string {
	const lower = word.toLowerCase();

	const uncountable = new Set(["series", "species", "news", "status"]);
	if (uncountable.has(lower)) {
		return word;
	}

	const irregular: Record<string, string> = {
		children: "child",
		people: "person",
		men: "man",
		women: "woman",
		mice: "mouse",
		geese: "goose",
		feet: "foot",
		teeth: "tooth",
		indices: "index",
		appendices: "appendix",
		matrices: "matrix",
		vertices: "vertex",
		analyses: "analysis",
		syntheses: "synthesis",
		diagnoses: "diagnosis",
		crises: "crisis",
		phenomena: "phenomenon",
		criteria: "criterion",
		data: "datum",
		alumni: "alumnus",
		cacti: "cactus",
		fungi: "fungus",
		nuclei: "nucleus",
		syllabi: "syllabus",
		bacteria: "bacterium",
		media: "medium",
	};

	if (irregular[lower]) {
		return applyOriginalCasing(word, irregular[lower]);
	}

	if (lower.endsWith("ies")) {
		const pre = lower[lower.length - 4] || "";
		const replacement = isVowel(pre) ? "ie" : "y";
		return word.slice(0, -3) + replacement;
	}

	if (lower.endsWith("es")) {
		const beforeEs = lower.slice(0, -2);
		if (/(ch|sh|x|z|ss)$/.test(beforeEs)) {
			return word.slice(0, -2);
		}
	}

	if (lower.endsWith("s") && !lower.endsWith("ss")) {
		return word.slice(0, -1);
	}

	return word;
}

function isVowel(c: string): boolean {
	return /[aeiou]/i.test(c);
}

function applyOriginalCasing(original: string, singularLower: string): string {
	if (!original) {
		return singularLower;
	}

	if (original === original.toUpperCase()) {
		return singularLower.toUpperCase();
	}

	const isCapitalized =
		original[0] === original[0]?.toUpperCase() &&
		original.slice(1) === original.slice(1).toLowerCase();

	if (isCapitalized) {
		return (
			singularLower.charAt(0).toUpperCase() +
			singularLower.slice(1).toLowerCase()
		);
	}

	return singularLower;
}

function sumNumbersInIsoString(iso: string): number {
	// Match ISO with timezone offset (e.g., 2025-08-10T00:59:26.238+02:00 or ...-02:00)
	const withTz = iso.match(
		/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?([+-])(\d{2}):(\d{2})$/,
	);
	if (withTz) {
		const [
			,
			year,
			month,
			day,
			hour,
			minute,
			second,
			millisecond = "0",
			tzSign,
			tzHour,
			tzMinute,
		] = withTz;
		const sign = tzSign === "-" ? -1 : 1;

		const baseSum =
			parseInt(year, 10) +
			parseInt(month, 10) +
			parseInt(day, 10) +
			parseInt(hour, 10) +
			parseInt(minute, 10) +
			parseInt(second, 10) +
			parseInt(millisecond, 10);

		return (
			baseSum + sign * parseInt(tzHour, 10) + sign * parseInt(tzMinute, 10)
		);
	}

	// Match ISO in Zulu time (e.g., 2025-08-10T00:59:26.238Z)
	const zulu = iso.match(
		/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?Z$/,
	);
	if (zulu) {
		const [, year, month, day, hour, minute, second, millisecond = "0"] = zulu;
		return (
			parseInt(year, 10) +
			parseInt(month, 10) +
			parseInt(day, 10) +
			parseInt(hour, 10) +
			parseInt(minute, 10) +
			parseInt(second, 10) +
			parseInt(millisecond, 10)
		);
	}

	// If not a recognized ISO string with timezone, return NaN
	return NaN;
}
